import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Loader2, Minus, Plus, Search, Send } from "lucide-react";
import { QrScanner } from "./QrScanner";
import { StampGrid } from "../components/StampGrid";
import { LOYALTY_GOAL } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const detail = (err) => err.response?.data?.detail || "Errore";

const CardActions = ({ card, auth, onChange }) => {
  const [busy, setBusy] = useState("");
  const act = async (path, body = {}) => {
    if (path === "redeem" && !window.confirm("Confermi lo sconto del 50%? La tessera ripartirà da zero.")) return;
    setBusy(path);
    try {
      const { data } = await axios.post(`${API}/admin/loyalty/${card.id}/${path}`, body, auth());
      if (path === "resend") toast.success("Tessera reinviata");
      else onChange(data);
      if (path === "stamp") toast.success(data.reward_available ? "10° timbro! Il prossimo taglio ha il 50% di sconto" : `Timbro aggiunto · ${data.stamps}/${LOYALTY_GOAL}`);
      if (path === "redeem") toast.success("Sconto 50% usato: tessera ripartita da zero");
    } catch (err) {
      if (path === "stamp" && err.response?.status === 409 && !card.reward_available && window.confirm(`${detail(err)}. Aggiungere comunque?`)) {
        setBusy("");
        return act("stamp", { force: true });
      }
      toast.error(detail(err));
    } finally {
      setBusy("");
    }
  };
  const btn = "inline-flex items-center justify-center gap-2 rounded-full px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-40";
  return (
    <div className="flex flex-wrap gap-2 mt-5">
      {card.reward_available ? (
        <button data-testid="loyalty-redeem-button" disabled={!!busy} onClick={() => act("redeem")} className={`${btn} flex-1 bg-[#C2410C] text-white hover:bg-[#9A3412]`}>
          {busy === "redeem" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />} Usa sconto 50%
        </button>
      ) : (
        <button data-testid="loyalty-stamp-button" disabled={!!busy} onClick={() => act("stamp")} className={`${btn} flex-1 bg-[#0B1F3A] text-white hover:bg-[#071528]`}>
          {busy === "stamp" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Aggiungi timbro
        </button>
      )}
      <button data-testid="loyalty-unstamp-button" disabled={!!busy || card.stamps === 0} onClick={() => act("unstamp")} className={`${btn} border border-[rgba(11,31,58,0.25)] text-[#0B1F3A] hover:border-[#0B1F3A]`}>
        <Minus className="w-4 h-4" /> Togli
      </button>
      <button data-testid="loyalty-resend-button" disabled={!!busy} onClick={() => act("resend")} className={`${btn} border border-[rgba(11,31,58,0.25)] text-[#0B1F3A] hover:border-[#0B1F3A]`}>
        <Send className="w-4 h-4" /> Reinvia
      </button>
    </div>
  );
};

const CardPanel = ({ card, auth, onChange }) => (
  <div data-testid="loyalty-scanned-card" className="rounded-2xl border border-[rgba(11,31,58,0.2)] bg-white p-5 sm:p-6">
    <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
      <div>
        <p className="font-semibold">{card.name || card.email}</p>
        <p className="text-xs text-[#6F6466]">{card.email}</p>
      </div>
      <p data-testid="loyalty-scanned-code" className="font-mono-alt text-sm tracking-[0.2em] text-[#0B1F3A]">{card.code}</p>
    </div>
    <StampGrid stamps={card.stamps} prefix="admin-stamp" />
    <p data-testid="loyalty-scanned-status" className="mt-4 text-sm">
      {card.reward_available ? <strong className="text-[#C2410C]">10 tagli raggiunti: questo taglio ha il 50% DI SCONTO</strong> : <>Timbri <strong>{card.stamps}/{LOYALTY_GOAL}</strong> · tagli totali {card.total_cuts} · sconti usati {card.rewards_redeemed}</>}
    </p>
    <CardActions card={card} auth={auth} onChange={onChange} />
  </div>
);

export const LoyaltyManager = ({ auth }) => {
  const [cards, setCards] = useState(null);
  const [card, setCard] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/admin/loyalty`, auth()).then((r) => setCards(r.data)).catch(() => setCards([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = async (q) => {
    if (!q.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/loyalty/lookup`, { ...auth(), params: { q: q.trim() } });
      setCard(data);
      setQuery("");
    } catch (err) {
      toast.error(detail(err));
    } finally {
      setBusy(false);
    }
  };

  const update = (c) => { setCard(c); setCards((list) => (list || []).map((x) => (x.id === c.id ? c : x))); };

  return (
    <section data-testid="loyalty-manager" className="grid grid-cols-1 lg:grid-cols-[minmax(0,420px)_1fr] gap-8">
      <div className="space-y-4 min-w-0">
        <QrScanner onScan={lookup} />
        <form onSubmit={(e) => { e.preventDefault(); lookup(query); }} className="flex gap-2">
          <input data-testid="loyalty-code-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Oppure scrivi il codice BANY-…"
            className="flex-1 min-w-0 rounded-full border border-[rgba(11,31,58,0.2)] bg-white px-5 py-3 text-sm outline-none focus:border-[#0B1F3A]" />
          <button data-testid="loyalty-code-search" disabled={busy} className="rounded-full border border-[#0B1F3A] text-[#0B1F3A] px-4 hover:bg-[#0B1F3A] hover:text-white transition-colors" aria-label="Cerca">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </button>
        </form>
        {card ? <CardPanel card={card} auth={auth} onChange={update} /> : <p className="text-sm text-[#6F6466]">Scansiona il QR della tessera del cliente per aggiungere un timbro a ogni taglio.</p>}
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-[0.2em] text-[#6F6466] mb-3">Tessere attive · {cards ? cards.length : "…"}</p>
        <div data-testid="loyalty-cards-list" className="rounded-2xl border border-[rgba(11,31,58,0.15)] bg-white divide-y divide-[rgba(26,20,22,0.06)] max-h-[560px] overflow-y-auto">
          {cards === null && <Loader2 className="m-5 w-5 h-5 animate-spin text-[#0B1F3A]" />}
          {cards?.length === 0 && <p className="p-5 text-sm text-[#6F6466]">Nessuna tessera ancora.</p>}
          {(cards || []).map((c) => (
            <button key={c.id} data-testid={`loyalty-card-row-${c.code}`} onClick={() => setCard(c)} className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-[#F7F2EC] transition-colors">
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold truncate">{c.name || c.email}</span>
                <span className="block text-xs text-[#6F6466] truncate">{c.code} · {c.email}{c.email_status === "failed" ? " · email non inviata" : ""}</span>
              </span>
              <span className={`font-mono-alt text-sm ${c.reward_available ? "text-[#C2410C] font-bold" : "text-[#0B1F3A]"}`}>{c.reward_available ? "−50%" : `${c.stamps}/${LOYALTY_GOAL}`}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
